import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QrScanner from 'react-qr-scanner';
import './QRScanner.css';

const QRScanner = () => {
  const navigate = useNavigate();
  const [scanResult, setScanResult] = useState(null);
  const [isScanning, setIsScanning] = useState(true);
  const [error, setError] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [facingMode, setFacingMode] = useState('environment'); // 'user' for front camera, 'environment' for back camera
  const [isProcessing, setIsProcessing] = useState(false);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      setIsProcessing(false);
    };
  }, []);

  const onScanSuccess = (data) => {
    if (data && data.text && !isProcessing) {
      console.log('QR Code scanned:', data.text);
      setScanResult(data.text);
      setIsScanning(false);
      
      // Process the scanned URL
      processScannedUrl(data.text);
    }
  };

  const onScanError = (error) => {
    // Handle scan errors - most are normal during scanning process
    console.warn('QR scan error:', error);
    
    // Only show camera-related errors to user
    if (error?.name === 'NotAllowedError') {
      setCameraError('Camera permission denied. Please allow camera access and refresh the page.');
      setIsScanning(false);
    } else if (error?.name === 'NotFoundError') {
      setCameraError('No camera found. Please make sure your device has a camera.');
      setIsScanning(false);
    } else if (error?.name === 'NotSupportedError') {
      setCameraError('Camera not supported in this browser. Please try a different browser.');
      setIsScanning(false);
    }
  };

  const processScannedUrl = async (url) => {
    // Prevent multiple simultaneous processing
    if (isProcessing) return;
    
    setIsProcessing(true);
    let token = null; // Declare token in outer scope
    
    try {
      // Check if it's a valid tour join URL - updated pattern to match actual token format
      const urlPattern = /\/join-tour\/([A-Za-z0-9_-]+)/;
      const match = url.match(urlPattern);
      
      if (match) {
        token = match[1];
      } else {
        // Check if it's a full URL that contains the join-tour path
        try {
          const urlObj = new URL(url);
          const pathMatch = urlObj.pathname.match(urlPattern);
          
          if (pathMatch) {
            token = pathMatch[1];
          }
        } catch (urlError) {
          // If it's not a valid URL, maybe it's just the token itself
          // Updated pattern to match various token formats
          if (/^[A-Za-z0-9_-]+$/.test(url.trim()) && url.trim().length >= 6) {
            token = url.trim();
          }
        }
      }
      
      if (!token) {
        setError('This QR code does not contain a valid tour link.');
        setIsProcessing(false);
        return;
      }

      console.log('Tour token found, validating:', token);
      
      // Clear any previous errors and show validating state
      setError('');
      setScanResult('Validating tour...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tour-guide/validate-qr/${token}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Token validation successful:', data);
        
        // Clear any errors and show success message
        setError('');
        setScanResult(`✅ Valid tour found!\n${data.tour.tour_name}\nby ${data.tour.guide_name}`);
        
        // Clear processing state before navigation
        setIsProcessing(false);
        
        // Navigate to join page
        setTimeout(() => {
          navigate(`/join-tour/${token}`);
        }, 1500);
        
      } else {
        const errorData = await response.json();
        console.error('Token validation failed:', errorData);
        
        // Show specific error messages based on response
        switch (response.status) {
          case 404:
            setError('This tour was not found or the link has expired.');
            break;
          case 400:
            if (errorData.message.includes('full')) {
              setError('Sorry, this tour is currently full. No more participants can join.');
            } else if (errorData.message.includes('no longer available')) {
              setError('This tour has ended and is no longer available.');
            } else {
              setError(errorData.message || 'This tour is not available.');
            }
            break;
          default:
            setError('Unable to validate this tour. Please try again.');
        }
        
        // Clear scan result when showing error
        setScanResult(null);
      }
      
    } catch (apiError) {
      console.error('Network error during validation:', apiError);
      
      // Only handle actual network/fetch errors
      if (apiError.name === 'AbortError' || apiError.name === 'TypeError' || apiError.message.includes('fetch')) {
        // Network error - offer to try anyway
        setError('Network error. Unable to validate tour. Would you like to try joining anyway?');
        setScanResult(null);
        
        // Add a "Try Anyway" button option if we have a token
        if (token) {
          setTimeout(() => {
            if (!isProcessing) return; // Check if component is still mounted
            if (window.confirm('Network error occurred. Would you like to try joining the tour anyway?')) {
              navigate(`/join-tour/${token}`);
            }
          }, 1000);
        }
      } else {
        console.error('Unexpected error processing scanned URL:', apiError);
        setError('Error processing QR code. Please try again.');
        setScanResult(null);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const restartScanning = () => {
    setScanResult(null);
    setError('');
    setCameraError('');
    setIsScanning(true);
    setIsProcessing(false);
  };

  const toggleCamera = () => {
    setFacingMode(facingMode === 'environment' ? 'user' : 'environment');
    // Brief pause to allow camera to switch
    setIsScanning(false);
    setTimeout(() => {
      setIsScanning(true);
    }, 100);
  };

  const handleManualInput = async () => {
    const input = prompt('Enter the tour link or token manually:');
    if (input?.trim()) {
      // Clear previous states
      setScanResult(null);
      setError('');
      setCameraError('');
      setIsProcessing(false);
      
      await processScannedUrl(input.trim());
    }
  };

  const previewStyle = {
    height: 300,
    width: '100%',
    maxWidth: 400,
    border: '2px solid #007bff',
    borderRadius: '12px',
    overflow: 'hidden'
  };

  const constraints = {
    audio: false,
    video: {
      facingMode: facingMode,
      width: { ideal: 640 },
      height: { ideal: 480 }
    }
  };

  return (
    <div className="qr-scanner-page">
      <div className="scanner-container">
        <div className="scanner-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1>Scan QR Code</h1>
          <p>Point your camera at a tour QR code to join</p>
        </div>

        <div className="scanner-content">
          {isScanning && !cameraError && (
            <div className="scanner-wrapper">
              <div className="camera-container">
                <QrScanner
                  delay={100}
                  style={previewStyle}
                  constraints={constraints}
                  onError={onScanError}
                  onScan={onScanSuccess}
                />
                <div className="scanner-overlay">
                  <div className="scanner-frame"></div>
                </div>
              </div>
              
              <div className="camera-controls">
                <button onClick={toggleCamera} className="camera-toggle-btn">
                  📷 Switch Camera ({facingMode === 'environment' ? 'Back' : 'Front'})
                </button>
              </div>
              
              <div className="scanner-instructions">
                <p>📱 Hold your phone steady</p>
                <p>🎯 Center the QR code in the frame</p>
                <p>💡 Make sure there's good lighting</p>
              </div>
            </div>
          )}

          {cameraError && (
            <div className="camera-error">
              <div className="error-icon">📷</div>
              <h3>Camera Error</h3>
              <p>{cameraError}</p>
              <div className="error-actions">
                <button onClick={() => window.location.reload()} className="retry-btn">
                  🔄 Refresh Page
                </button>
                <button onClick={handleManualInput} className="manual-btn">
                  ⌨️ Enter Manually
                </button>
              </div>
            </div>
          )}

          {scanResult && !error && (
            <div className="scan-result">
              <div className="result-icon">
                {scanResult.includes('Validating') ? '🔄' : '✅'}
              </div>
              <h3>
                {scanResult.includes('Validating') ? 'Validating...' : 'Tour Found!'}
              </h3>
              <div className="scanned-data">
                {scanResult}
              </div>
              {scanResult.includes('Validating') && (
                <div className="loading-spinner"></div>
              )}
            </div>
          )}

          {error && (
            <div className="scan-error">
              <div className="error-icon">❌</div>
              <h3>Scan Error</h3>
              <p>{error}</p>
              <div className="error-actions">
                <button onClick={restartScanning} className="retry-btn">
                  📷 Try Again
                </button>
                <button onClick={handleManualInput} className="manual-btn">
                  ⌨️ Enter Manually
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="scanner-tips">
          <h3>💡 Tips for better scanning</h3>
          <ul>
            <li>Make sure the QR code is clean and not damaged</li>
            <li>Hold your phone 6-12 inches away from the code</li>
            <li>Ensure good lighting conditions</li>
            <li>Keep your phone steady while scanning</li>
            <li>Try switching between front and back camera if one doesn't work</li>
          </ul>
          
          <div className="manual-option">
            <button onClick={handleManualInput} className="manual-input-btn">
              Can't scan? Enter tour link manually
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;